import {
  Breadcrumb,
  Button,
  Col,
  Form,
  Row,
  Space,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import Data from "../controls/Data";
import { retry } from "../utils/http";
import { DataAction, DataColumn, DataRequest } from "../controls/Data.default";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  getCompanies,
  getCompanyDepartments,
  getPlatformUsers,
  getCompanyIndustries,
  getCompanyUserRoles,
} from "../repository/data.repository";
import {
  companiesDepartmentViewColumns,
  companiesUserViewColumns,
  companiesViewColumns,
  CompanyViewCrumb,
  mapCompaniesViewQuery,
  mapDepartmentsViewQuery,
  mapUsersViewQuery,
} from "./CompaniesView.default";
import {
  Company,
  Department,
  PlatformUser,
} from "../repository/data.default";
import { nameOf } from "../utils/typescript";
import {
  createScopedSearchRequest,
  createSearchRequest,
} from "../hooks/useSearchRequest";
import DynamicForm from "../controls/DynamicForm";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

export interface CompaniesViewProps {
  readonly trigger?: string;
  readonly title: string;
  readonly panel: React.ReactNode;
  readonly onAction?: (key: string, record: any) => void;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
  readonly onCrumb: (crumb: CompanyViewCrumb) => void;
}

function CompaniesView(props: CompaniesViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const permissions = useSelector(authorizeSelectors.permissions);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
    readonly crumb: {
      readonly key: React.Key;
      readonly title: string;
    };
    readonly rowKey?: React.Key;
  }>({
    searchQuery: {
      conditions: [],
    },
    crumb: {
      key: "_",
      title: "Search",
    },
  });
  const [valuesValid, setValuesValid] = useState(false);
  const [crumbs, setCrumbs] = useState<
    {
      readonly key: React.Key;
      readonly title: string;
      readonly request: string;
    }[]
  >([
    {
      key: "_",
      title: "Search",
      request: "search",
    },
  ]);

  useEffect(() => {
    const crumb = crumbs.slice(-1)[0];

    props.onCrumb?.(crumb as CompanyViewCrumb);
  }, []);

  useEffect(() => {
    const crumb = crumbs.slice(-1)[0];

    props.onCrumb?.(crumb as CompanyViewCrumb);
  }, [crumbs]);

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  useEffect(() => {
    setContext({ ...context });
  }, [props.trigger]);

  // Events

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const crumb = crumbs.slice(-1)[0];
    switch ((crumb as any).request) {
      case "subcompany":
        {
          const searchQuery = mapCompaniesViewQuery(values);
          setContext({
            crumb,
            searchQuery,
          });
          props.onSearch?.(searchQuery);
        }
        break;
      case "department":
        {
          const searchQuery = mapDepartmentsViewQuery(values);
          setContext({
            crumb,
            searchQuery,
          });
          props.onSearch?.(searchQuery);
        }
        break;
      case "user":
        {
          const searchQuery = mapUsersViewQuery(values);
          setContext({
            crumb,
            searchQuery,
          });
          props.onSearch?.(searchQuery);
        }
        break;

      default:
        {
          const searchQuery = mapCompaniesViewQuery(values);
          setContext({
            crumb,
            searchQuery,
          });

          props.onSearch?.(searchQuery);
        }
        break;
    }
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({
      ...context,
      searchQuery: {
        conditions: [],
      },
    });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  function onPrevious(key: React.Key): void {
    const index = crumbs.findIndex((crumb) => crumb.key === key);
    const crumb = crumbs[index];
    setCrumbs(crumbs.slice(0, index + 1));
    setContext({ ...context, crumb, searchQuery: { conditions: [] } });
    form.resetFields();
  }

  function onNext(
    key: React.Key,
    record: {
      readonly __type: string;
      readonly uid: string;
      readonly name: string;
      readonly subCompaniesCount?: number;
      readonly departmentsCount?: number;
      readonly usersCount?: number;
    },
    request: string,
    direct?: boolean,
  ): void {
    if (["company", "subcompany", "department"].includes(record.__type)) {
      let crumb = {
        key: record.uid,
        title: record.name,
        type: record.__type,
        request,
      };

      if (!direct) {
        if (record.__type === "subcompany") {
          if (record.departmentsCount || !record.usersCount) {
            crumb = {
              key: record.uid,
              title: record.name,
              type: record.__type,
              request: "department",
            };
          } else if (record.usersCount) {
            crumb = {
              key: record.uid,
              title: record.name,
              type: record.__type,
              request: "user",
            };
          }
        } else if (record.__type === "department") {
          if (record.usersCount) {
            crumb = {
              key: record.uid,
              title: record.name,
              type: record.__type,
              request: "user",
            };
          }
        } else {
          if (record.departmentsCount || !record.subCompaniesCount) {
            crumb = {
              key: record.uid,
              title: record.name,
              type: record.__type,
              request: "department",
            };
          } else if (record.subCompaniesCount) {
            crumb = {
              key: record.uid,
              title: record.name,
              type: record.__type,
              request: "subcompany",
            };
          }
        }
      }

      setCrumbs([...crumbs, crumb]);
      setContext({ ...context, crumb, searchQuery: { conditions: [] } });
      form.resetFields();
    }
  }

  // Methods

  const request = ({ context, pageNumber, pageSize, sort }: DataRequest) => {
    const sortBy = (sort || [])[0]?.key ?? null;
    const sortDescending = (sort || [])[0]?.direction === "desc";
    const { crumb, searchQuery } = context;

    switch (crumb.request) {
      case "company":
        return retry(() =>
          getCompanies({
            searchQuery: {
              ...searchQuery,
              conditions: [
                ...searchQuery.conditions,
                {
                  field: nameOf<Company>("parentCompanyUid"),
                  values: [crumb.key],
                  operation: QueryOperation.Equal,
                },
              ],
            },
            pageNumber,
            pageSize,
            ...(sortBy
              ? {
                  sortBy,
                  sortDescending,
                }
              : {}),
          }),
        ).then(({ data, totalRecords }) => ({
          data: data.map((record) => ({ ...record, __type: "subcompany" })),
          pageNumber,
          pageSize,
          total: totalRecords,
        }));
      case "subcompany":
        return retry(() =>
          getCompanies({
            searchQuery: {
              ...searchQuery,
              conditions: [
                ...searchQuery.conditions,
                {
                  field: nameOf<Company>("parentCompanyUid"),
                  values: [crumb.key],
                  operation: QueryOperation.Equal,
                },
              ],
            },
            pageNumber,
            pageSize,
            ...(sortBy
              ? {
                  sortBy,
                  sortDescending,
                }
              : {}),
          }),
        ).then(({ data, totalRecords }) => ({
          data: data.map((record) => ({ ...record, __type: "subcompany" })),
          pageNumber,
          pageSize,
          total: totalRecords,
        }));
      case "department":
        return retry(() =>
          getCompanyDepartments({
            searchQuery: {
              ...searchQuery,
              conditions: [
                ...searchQuery.conditions,
                {
                  field: nameOf<Department>("companyUid"),
                  values: [crumb.key],
                  operation: QueryOperation.Equal,
                },
              ],
            },
            pageNumber,
            pageSize,
            ...(sortBy
              ? {
                  sortBy,
                  sortDescending,
                }
              : {}),
          }),
        ).then(({ data, totalRecords }) => ({
          data: data.map((record) => ({ ...record, __type: "department" })),
          pageNumber,
          pageSize,
          total: totalRecords,
        }));
      case "user": {
        // The user layer is reachable two ways: from a Department (scope by departmentUid) or
        // directly from a (sub)company that has users but no departments (scope by companyUid).
        // Pick the column from the crumb that opened it — a company-keyed drill filtered by
        // departmentUid would match nothing.
        const usersScopeField =
          (crumb as any).type === "department"
            ? nameOf<PlatformUser>("departmentUid")
            : nameOf<PlatformUser>("companyUid");
        return retry(() =>
          getPlatformUsers({
            searchQuery: {
              ...searchQuery,
              conditions: [
                ...searchQuery.conditions,
                {
                  field: usersScopeField,
                  values: [crumb.key],
                  operation: QueryOperation.Equal,
                },
              ],
            },
            pageNumber,
            pageSize,
            ...(sortBy
              ? {
                  sortBy,
                  sortDescending,
                }
              : {}),
          }),
        ).then(({ data, totalRecords }) => ({
          data: data.map((record) => ({ ...record, __type: "user" })),
          pageNumber,
          pageSize,
          total: totalRecords,
        }));
      }

      default:
        return retry(() =>
          getCompanies({
            searchQuery,
            pageNumber,
            pageSize,
            ...(sortBy
              ? {
                  sortBy,
                  sortDescending,
                }
              : {}),
          }),
        ).then(({ data, totalRecords }) => ({
          data: data.map((record) => ({ ...record, __type: "company" })),
          pageNumber,
          pageSize,
          total: totalRecords,
        }));
    }
  };

  const industriesRequest = createSearchRequest(getCompanyIndustries, "name");
  const companiesRequest = createSearchRequest(getCompanies, "name");
  // Department and Role are scoped to the company picked above in this same filter, so the user
  // can't pair company X with a department/role from company Y. `dependsOn: ["company"]` on the
  // two controls clears a stale child when the company changes.
  const getSelectedCompany = () => form.getFieldValue("company")?.value as string | undefined;
  const departmentsRequest = createScopedSearchRequest(
    getCompanyDepartments,
    "name",
    "companyUid",
    getSelectedCompany,
  );
  const userRolesRequest = createScopedSearchRequest(
    getCompanyUserRoles,
    "name",
    "companyUid",
    getSelectedCompany,
  );

  const crumb = crumbs.slice(-1)[0];
  let columns: DataColumn[] = [];
  let actions: DataAction[] = [];
  switch ((crumb as any).request) {
    case "subcompany":
      columns = companiesViewColumns;
      // No row-level "Edit": company fields (name/address/email/phone) are edited inline
      // on the profile (View), and Industry/Parent are locked post-registration — so a
      // separate edit modal would be redundant and confusing.
      actions = [
        {
          key: "previewDepartments",
          label: "View Departments",
          handler: (key, record) => onNext(key, record, "department", true),
        },
        {
          key: "view",
          label: "View",
          handler: onAction,
        },
        {
          key: "delete",
          label: "Delete",
          danger: true,
          disabled: !permissions.can("Companies", "delete"),
          handler: onAction,
        },
      ];
      break;
    case "department":
      columns = companiesDepartmentViewColumns;
      // No row-level "Edit": a department's only editable field (Name) is edited inline
      // on its profile (View), so a separate edit modal would be redundant.
      actions = [
        {
          key: "previewUsers",
          label: "View Users",
          handler: (key, record) => onNext(key, record, "user", true),
        },
        {
          key: "view",
          label: "View",
          handler: onAction,
        },
        {
          key: "delete",
          label: "Delete",
          danger: true,
          disabled: !permissions.can("CompanyDepartments", "delete"),
          handler: onAction,
        },
      ];
      break;
    case "user":
      columns = companiesUserViewColumns;
      actions = [
        {
          key: "view",
          label: "View",
          handler: onAction,
        },

        {
          key: "delete",
          label: "Delete",
          danger: true,
          disabled: !permissions.can("Companies", "delete"),
          handler: onAction,
        },
      ];
      break;
    default:
      columns = companiesViewColumns;
      // No row-level "Edit": company fields (name/address/email/phone) are edited inline
      // on the profile (View), and Industry/Parent are locked post-registration — so a
      // separate edit modal would be redundant and confusing.
      actions = [
        {
          key: "previewDepartments",
          label: "View Departments",
          handler: (key, record) => onNext(key, record, "department", true),
        },
        {
          key: "view",
          label: "View",
          handler: onAction,
        },
        {
          key: "delete",
          label: "Delete",
          danger: true,
          disabled: !permissions.can("Companies", "delete"),
          handler: onAction,
        },
      ];
      break;
  }

  return (
    <Col className="flex flex-auto flex-col flex-nowrap overflow-hidden">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4 pl-0 pr-0">
        <Typography.Title className="m-0" level={4}>
          {props.title}
        </Typography.Title>
        {crumbs.length > 1 && (
          <Breadcrumb
            className="ml-8 mr-4"
            items={crumbs}
            itemRender={({ title, key, type }: any) => (
              <Tooltip title={title} placement="bottom">
                <Button
                  className={`max-w-64`}
                  type="text"
                  size={"small"}
                  onClick={() => onPrevious(key!)}
                >
                  <span>
                    <span
                      className="mr-1 text-sm uppercase"
                      style={{
                        color: token.colorPrimary,
                      }}
                    >
                      {type}
                    </span>
                    <span>{title}</span>
                  </span>
                </Button>
              </Tooltip>
            )}
          />
        )}
        <Row className="flex flex-auto flex-row">{props.panel}</Row>
      </Row>
      <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
        {/* Form */}
        <Col
          className="mr-4 flex flex-col border border-solid pl-2 pr-2"
          style={{
            borderColor: token.colorBorder,
            borderRadius: token.borderRadius,
          }}
        >
          <Space className="flex flex-row justify-end pb-2 pt-2">
            <Button disabled={loading} onClick={onClearData}>
              Clear
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={!valuesValid}
              onClick={onSearchData}
            >
              Search
            </Button>
          </Space>

          {(() => {
            switch (crumb.request) {
              case "subcompany":
                return (
                  <DynamicForm
                    key={crumb.request}
                    className="w-72 overflow-y-auto"
                    form={form}
                    schema={{
                      groups: [
                        {
                          controls: [
                            {
                              type: "textbox",
                              name: "name",
                              label: "Name",
                            },
                            {
                              type: "textbox",
                              name: "email",
                              label: "Email",
                            },
                            {
                              type: "textbox",
                              name: "address",
                              label: "Address",
                            },
                            {
                              type: "textbox",
                              name: "phoneNumber",
                              label: "Phone Number",
                            },
                            {
                              type: "combobox",
                              name: "domain",
                              label: "Industry",
                              request: industriesRequest,
                            },
                          ],
                        },
                      ],
                    }}
                  />
                );
              case "department":
                return (
                  <DynamicForm
                    key={crumb.request}
                    className="w-72 overflow-y-auto"
                    form={form}
                    schema={{
                      groups: [
                        {
                          controls: [
                            {
                              type: "textbox",
                              name: "name",
                              label: "Name",
                            },
                          ],
                        },
                      ],
                    }}
                  />
                );
              case "user":
                return (
                  <DynamicForm
                    key={crumb.request}
                    className="w-72 overflow-y-auto"
                    form={form}
                    schema={{
                      groups: [
                        {
                          controls: [
                            {
                              type: "combobox",
                              name: "company",
                              label: "Company",
                              request: companiesRequest,
                            },
                            {
                              type: "combobox",
                              name: "role",
                              label: "Role",
                              request: userRolesRequest,
                              dependsOn: ["company"],
                            },
                            {
                              type: "combobox",
                              name: "department",
                              label: "Department",
                              request: departmentsRequest,
                              dependsOn: ["company"],
                            },
                            {
                              type: "textbox",
                              name: "email",
                              label: "Email",
                            },
                            {
                              type: "textbox",
                              name: "firstName",
                              label: "First Name",
                            },
                            {
                              type: "textbox",
                              name: "lastName",
                              label: "Last Name",
                            },
                            {
                              type: "datepicker",
                              name: "dateOfBirth",
                              label: "Date Of Birth",
                            },
                          ],
                        },
                      ],
                    }}
                  />
                );

              // The top-level companies list. Parent Company and Created On are filterable only here:
              // the sub-company drill-down is already scoped to one parent, so filtering it by parent
              // would be a control that can only ever narrow to what is already on screen.
              default:
                return (
                  <DynamicForm
                    className="w-72 overflow-y-auto"
                    form={form}
                    schema={{
                      groups: [
                        {
                          controls: [
                            {
                              type: "textbox",
                              name: "name",
                              label: "Name",
                            },
                            {
                              type: "textbox",
                              name: "address",
                              label: "Address",
                            },
                            {
                              type: "textbox",
                              name: "email",
                              label: "Email",
                            },
                            {
                              type: "textbox",
                              name: "phoneNumber",
                              label: "Phone Number",
                            },
                            {
                              type: "combobox",
                              name: "domain",
                              label: "Industry",
                              request: industriesRequest,
                            },
                            {
                              type: "combobox",
                              name: "parentCompany",
                              label: "Parent Company",
                              request: companiesRequest,
                            },
                            {
                              type: "rangepicker",
                              name: "createdOn",
                              label: "Created On",
                            },
                          ],
                        },
                      ],
                    }}
                  />
                );
            }
          })()}
        </Col>
        {/* Data */}
        <Data
          columns={columns}
          context={context}
          pageSize={25}
          actions={actions}
          request={request}
          rowKey={({ uid }) => uid}
          onLoading={onLoading}
          onDbSelect={(key, record) => onNext(key, record, record.__type)}
        />
      </Row>
    </Col>
  );
}

export default CompaniesView;
